<template>
	<EventLayout :event="event" :subtitle="pack.name" :return-url="route('events.packs.index', event.id)" :return-label="trans('events.packs.title')">
		<template #actions>
			<Link v-if="pendingEnrollmentsCount > 0" :as="PrimaryButton" :color="Color.Yellow" :href="route('events.packs.enrollments.index', [event.id, pack.id, EnrollmentStatus.Pending])">
				{{ transChoice('events.packs.enrollments.pending.count', pendingEnrollmentsCount) }}
			</Link>
			<Link v-if="incompleteEnrollmentsCount > 0" :as="PrimaryButton" :color="Color.Orange" :href="route('events.packs.enrollments.index', [event.id, pack.id, EnrollmentStatus.Incomplete])">
				{{ transChoice('events.packs.enrollments.incomplete.count', incompleteEnrollmentsCount) }}
			</Link>
			<PrimaryButton :color="Color.Green" @click.stop="save">
				<i class="fa-solid fa-save mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
			<LabeledObject :description="trans('events.packs.name_description')" required>
				<template #label>{{ trans('events.packs.name') }}</template>
				<input v-model="pack.name" type="text">
			</LabeledObject>
			<LabeledObject :description="trans('events.packs.day_description')">
				<template #label>
					{{ trans('events.packs.day') }}
					<button v-if="pack.day !== null" class="hover:text-red-600 dark:hover:text-red-400 underline ml-2" @click.stop="pack.day = null">{{ trans('general.remove') }}</button>
				</template>
				<input v-model="pack.day" type="date">
			</LabeledObject>
			<Switch v-model.number="pack.active" :label="trans('general.enabled')"/>
			<Switch v-model.number="pack.mandatory" :label="trans('events.packs.mandatory')"/>
			<Card :title="trans('events.payables.prices.early')">
				<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.general') }}</template>
						<NumberInput v-model.number="pack.early_general_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.local') }}</template>
						<NumberInput v-model.number="pack.early_local_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.international') }}</template>
						<NumberInput v-model.number="pack.early_international_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.representative') }}</template>
						<NumberInput v-model.number="pack.early_representative_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.organizer') }}</template>
						<NumberInput v-model.number="pack.early_organizer_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.workgroup') }}</template>
						<NumberInput v-model.number="pack.early_workgroup_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.board') }}</template>
						<NumberInput v-model.number="pack.early_board_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.dinosaur') }}</template>
						<NumberInput v-model.number="pack.early_dinosaur_price"/>
					</LabeledObject>
				</div>
			</Card>
			<Card :title="trans('events.payables.prices.late')">
				<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.general') }}</template>
						<NumberInput v-model.number="pack.general_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.local') }}</template>
						<NumberInput v-model.number="pack.local_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.international') }}</template>
						<NumberInput v-model.number="pack.international_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.representative') }}</template>
						<NumberInput v-model.number="pack.representative_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.organizer') }}</template>
						<NumberInput v-model.number="pack.organizer_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.workgroup') }}</template>
						<NumberInput v-model.number="pack.workgroup_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.board') }}</template>
						<NumberInput v-model.number="pack.board_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.dinosaur') }}</template>
						<NumberInput v-model.number="pack.dinosaur_price"/>
					</LabeledObject>
				</div>
			</Card>
		</div>
		<ManageActivities :event="event" :pack="pack" :activities="pack.activities"/>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {Link} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import {defineProps, ref, type Ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import Card from '@/Components/UI/Cards/Card.vue'
import {trans, transChoice} from 'laravel-vue-i18n'
import {type Pack} from '@/Types/Events/Packs/Pack'
import EventLayout from '@/Layouts/EventLayout.vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {EnrollmentStatus} from '@/Types/Enums/EnrollmentStatus'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import ManageActivities from '@/Components/Events/Packs/Activities/ManageActivities.vue'

const props = defineProps<{
	pack:Pack,
	event:Event,
	pendingEnrollmentsCount:number,
	incompleteEnrollmentsCount:number,
}>()

const pack:Ref<Pack> = ref(props.pack)
if(pack.value.day !== null){
	pack.value.day = dayjs(pack.value.day).format('YYYY-MM-DD')
}
const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)

function save():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.packs.update', {
			event: event.value.id,
			pack: pack.value.id,
		}), pack.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>